import React, { ReactNode, useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useModule } from '../context/ModuleContext';
import { useTill } from '../context/TillContext';
import TillStatusPill from './TillStatusPill';
import AppLogo from '../images/logo.png';
import {
  ShoppingBag, History, LogOut, Settings,
  UserPlus, Users, CreditCard, XCircle,
  ChevronDown,
} from 'lucide-react';
import './CashierLayout.css';

interface CashierLayoutProps {
  children: ReactNode;
}

const CashierLayout: React.FC<CashierLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { company } = useModule();
  const { activeTill } = useTill();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [panelOpen, setPanelOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // "Change Waiter" (PIN staff switch) is a restaurant-floor concept — non-restaurant
  // POS verticals have no waiters, so the Staff section is hidden for them.
  const isRestaurant = (company?.moduleCode ?? '').toLowerCase() === 'restaurant';

  // Close panel on outside click
  useEffect(() => {
    if (!panelOpen) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [panelOpen]);

  // Close panel on route change
  useEffect(() => {
    setPanelOpen(false);
  }, [location.pathname]);

  const go = (path: string) => {
    setPanelOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="cashier-shell">
      {/* ── Top bar ─────────────────────────────────────────────── */}
      <header className="cashier-header">
        <div className="cashier-header-left">
          <img src={AppLogo} alt="IMS" className="cashier-logo" />
          {company?.name && (
            <span className="cashier-company">{company.name}</span>
          )}
        </div>

        <nav className="cashier-nav">
          <NavLink
            to="/cashier/order"
            className={({ isActive }) =>
              `cashier-nav-link${isActive ? ' cashier-nav-link--active' : ''}`
            }
          >
            <ShoppingBag size={16} strokeWidth={1.75} />
            {t('cashier.order', 'Order')}
          </NavLink>

          {/* Bill History intentionally removed from the cashier — reprinting past
              bills is a fraud vector, so cashiers cannot browse or reprint history. */}

          {/* ── Control Panel ──────────────────────────────────── */}
          <div className="cashier-cp-wrapper" ref={panelRef}>
            <button
              className={`cashier-nav-link cashier-cp-btn${panelOpen ? ' cashier-nav-link--active' : ''}`}
              onClick={() => setPanelOpen((p) => !p)}
            >
              <Settings size={16} strokeWidth={1.75} />
              {t('cashier.controlPanel', 'Control Panel')}
              <ChevronDown
                size={13}
                strokeWidth={2}
                className={`cashier-cp-chevron${panelOpen ? ' cashier-cp-chevron--open' : ''}`}
              />
            </button>

            {panelOpen && (
              <div className="cashier-cp-panel">
                {/* ── Till ─────────────────────────── */}
                <div className="cashier-cp-section">
                  <span className="cashier-cp-section-label">
                    <CreditCard size={12} strokeWidth={2} />
                    {t('cashier.cp.till', 'Till')}
                  </span>
                  {!activeTill ? (
                    <button className="cashier-cp-item cashier-cp-item--amber" onClick={() => go('/sales/till/open')}>
                      <CreditCard size={15} strokeWidth={1.75} />
                      {t('cashier.cp.openTill', 'Open Till')}
                    </button>
                  ) : (
                    <button className="cashier-cp-item cashier-cp-item--red" onClick={() => go('/sales/till/close')}>
                      <XCircle size={15} strokeWidth={1.75} />
                      {t('cashier.cp.closeTill', 'Close Till')}
                    </button>
                  )}
                  <button className="cashier-cp-item" onClick={() => go('/sales/till/history')}>
                    <History size={15} strokeWidth={1.75} />
                    {t('cashier.cp.tillHistory', 'Till History')}
                  </button>
                </div>

                <div className="cashier-cp-divider" />

                {/* ── Customers ────────────────────── */}
                <div className="cashier-cp-section">
                  <span className="cashier-cp-section-label">
                    <Users size={12} strokeWidth={2} />
                    {t('cashier.cp.customers', 'Customers')}
                  </span>
                  <button className="cashier-cp-item cashier-cp-item--blue" onClick={() => go('/customers/create')}>
                    <UserPlus size={15} strokeWidth={1.75} />
                    {t('cashier.cp.addCustomer', 'Add Customer')}
                  </button>
                  <button className="cashier-cp-item" onClick={() => go('/customers')}>
                    <Users size={15} strokeWidth={1.75} />
                    {t('cashier.cp.customerList', 'Customer List')}
                  </button>
                </div>

                {/* ── Staff (restaurant only) ──────── */}
                {isRestaurant && (
                  <>
                    <div className="cashier-cp-divider" />
                    <div className="cashier-cp-section">
                      <span className="cashier-cp-section-label">
                        <Users size={12} strokeWidth={2} />
                        {t('cashier.cp.staff', 'Staff')}
                      </span>
                      <button className="cashier-cp-item cashier-cp-item--green" onClick={() => go('/cashier/order')}>
                        <Users size={15} strokeWidth={1.75} />
                        {t('cashier.cp.changeWaiter', 'Change Waiter')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </nav>

        <div className="cashier-header-right">
          <TillStatusPill />
          <span className="cashier-user">
            {user?.firstName} {user?.lastName}
          </span>
          <button className="cashier-logout-btn" onClick={handleLogout} title="Logout">
            <LogOut size={15} strokeWidth={1.75} />
            {t('common.logout', 'Logout')}
          </button>
        </div>
      </header>

      {/* ── Page content ────────────────────────────────────────── */}
      <main className="cashier-content">
        {children}
      </main>
    </div>
  );
};

export default CashierLayout;
